// src/pages/recruiter-management/components/EditRecruiterModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { recruiterAPI } from '../../../utils/api';
import Notifier from '../../../components/ui/Notifier';

const EditRecruiterModal = ({ isOpen, onClose, recruiter, onUpdated }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (recruiter) {
      setFormData({
        name: recruiter.name || '',
        address: recruiter.address || '',
        country: recruiter.country || '',
        representative_name: recruiter.representative_name || '',
        contact_no: recruiter.contact_no || '',
        fwcms_registered_name: recruiter.fwcms_registered_name || '',
        point_of_entry: recruiter.point_of_entry || '',
        embassy: recruiter.embassy || '',
      });
    }
  }, [recruiter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const response = await recruiterAPI.updateRecruiter(recruiter.id, formData);
      if (response.success || response.data) {
        setNotification({ show: true, message: 'Recruiter updated successfully!', type: 'success' });
        setTimeout(() => {
          onUpdated(response.data);
          onClose();
        }, 1500);
      } else {
        setErrors(response.errors || { general: 'An unknown error occurred.' });
        setNotification({ show: true, message: response.message || 'Update failed.', type: 'error' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'An unexpected error occurred.';
      setErrors({ general: errorMsg });
      setNotification({ show: true, message: errorMsg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Country options based on the same requirements
  const countries = [
    'Bangladesh',
    'India', 
    'Indonesia',
    'Myanmar',
    'Nepal',
    'Pakistan',
    'Philippines',
    'Sri Lanka',
    'Thailand',
    'Vietnam'
  ];

  // Point of Entry options - only KLIA and KLIA2
  const pointOfEntryOptions = [
    'KLIA',
    'KLIA2'
  ];

  // Embassy options
  const embassyOptions = [
    'Embassy of Malaysia in Dhaka',
    'Embassy of Malaysia in New Delhi', 
    'Embassy of Malaysia in Jakarta',
    'Embassy of Malaysia in Yangon',
    'Embassy of Malaysia in Kathmandu',
    'Embassy of Malaysia in Islamabad',
    'Embassy of Malaysia in Manila',
    'Embassy of Malaysia in Colombo',
    'Embassy of Malaysia in Bangkok',
    'Embassy of Malaysia in Hanoi',
    'Other'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Recruiter Profile</h2>
            <p className="text-sm text-gray-500 mt-1">Update the details for {recruiter?.name}</p>
          </div>
          <button onClick={onClose} disabled={loading} className="p-2 hover:bg-gray-100 rounded-lg">
            <Icon name="X" size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-error-600 mr-2" />
                  <span className="text-sm text-error-700">{errors.general}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary border-b border-secondary-200 pb-2">
                Update Recruiter Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recruiter Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Recruiter Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter recruiter/agency name"
                    disabled={loading}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error-600">{errors.name}</p>
                  )}
                </div>

                {/* FWCMS Registered Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    FWCMS Registered Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fwcms_registered_name"
                    value={formData.fwcms_registered_name || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.fwcms_registered_name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter FWCMS registered name"
                    disabled={loading}
                  />
                  {errors.fwcms_registered_name && (
                    <p className="mt-1 text-sm text-error-600">{errors.fwcms_registered_name}</p>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Address <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.address ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter complete address"
                    disabled={loading}
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-error-600">{errors.address}</p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Source Country <span className="text-error-500">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.country ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select source country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="mt-1 text-sm text-error-600">{errors.country}</p>
                  )}
                </div>

                {/* Representative Name */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Representative Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="representative_name"
                    value={formData.representative_name || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.representative_name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter representative name"
                    disabled={loading}
                  />
                  {errors.representative_name && (
                    <p className="mt-1 text-sm text-error-600">{errors.representative_name}</p>
                  )}
                </div>

                {/* Contact Number */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Contact Number <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_no"
                    value={formData.contact_no || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.contact_no ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter contact number (e.g., +60123456789)"
                    disabled={loading}
                  />
                  {errors.contact_no && (
                    <p className="mt-1 text-sm text-error-600">{errors.contact_no}</p>
                  )}
                </div>

                {/* Point of Entry */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Point of Entry <span className="text-error-500">*</span>
                  </label>
                  <select
                    name="point_of_entry"
                    value={formData.point_of_entry || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.point_of_entry ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select point of entry</option>
                    {pointOfEntryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.point_of_entry && (
                    <p className="mt-1 text-sm text-error-600">{errors.point_of_entry}</p>
                  )}
                </div>

                {/* Embassy */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Embassy at Source Country (FWCMS) <span className="text-secondary-500">(Optional)</span>
                  </label>
                  <select
                    name="embassy"
                    value={formData.embassy || ''}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.embassy ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select embassy (optional)</option>
                    {embassyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.embassy && (
                    <p className="mt-1 text-sm text-error-600">{errors.embassy}</p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button type="button" onClick={onClose} disabled={loading} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300">
            {loading ? 'Updating...' : 'Save Changes'}
          </button>
        </div>
      </div>
      <Notifier
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, show: false })}
      />
    </div>
  );
};

export default EditRecruiterModal;